import { Injectable, computed, signal } from '@angular/core';
import { finalize, interval, tap } from 'rxjs';
import { TimeTrackingBarApi, ApiWorkTimeDto } from './time-tracking-bar.api';

export type UiState = 'inactive' | 'running' | 'paused';

interface Snapshot {
    uiState: UiState;
    workedSeconds: number; // inkl. Offset zum Zeitpunkt der letzten Hydration
    pauseSeconds: number;  // dito
}

@Injectable({ providedIn: 'root' })
export class TimeTrackingBarService {
    constructor(private api: TimeTrackingBarApi) {
        this.refresh();
        interval(1000).subscribe(() => {
            if (this.loading()) return;
            const s = this.snapshot();
            if (s.uiState === 'running') this.elapsedTimeSec.update(v => v + 1);
            if (s.uiState === 'paused') this.totalPauseSec.update(v => v + 1);
        });
    }

    // ===== Signals =====
    private snapshot = signal<Snapshot>({ uiState: 'inactive', workedSeconds: 0, pauseSeconds: 0 });
    private loading = signal(false);

    private elapsedTimeSec = signal(0);
    private totalPauseSec = signal(0);

    // ===== Derived (Getter für Templates) =====
    isRunning = computed(() => this.snapshot().uiState === 'running');
    isPaused = computed(() => this.snapshot().uiState === 'paused');
    apiLoading = computed(() => this.loading());

    elapsedSeconds = computed(() => this.elapsedTimeSec());
    totalPauseSeconds = computed(() => this.totalPauseSec());

    // ===== Public API =====
    refresh(): void {
        this.loading.set(true);
        this.api.getCurrent().pipe(
            tap(dto => this.hydrateFromApi(dto)),
            finalize(() => this.loading.set(false))
        ).subscribe();
    }

    start(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this.api.start().pipe(
            tap(dto => this.hydrateFromApi(dto)),
            finalize(() => this.loading.set(false))
        ).subscribe();
    }

    pause(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this.api.pause().pipe(
            tap(dto => this.hydrateFromApi(dto)),
            finalize(() => this.loading.set(false))
        ).subscribe();
    }

    resume(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this.api.resume().pipe(
            tap(dto => this.hydrateFromApi(dto)),
            finalize(() => this.loading.set(false))
        ).subscribe();
    }

    stop(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this.api.stop().pipe(
            tap(dto => this.hydrateFromApi(dto)),
            finalize(() => this.loading.set(false))
        ).subscribe();
    }

    // ===== Utils (Format) =====
    formatHM(totalSeconds: number): string {
        const s = Math.max(0, Math.abs(totalSeconds|0));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    formatHMS(totalSeconds: number): string {
        const s = Math.max(0, Math.abs(totalSeconds|0));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }

    // ===== Mapping =====
    private hydrateFromApi(dto: ApiWorkTimeDto | null): void {
        const accumulatedWorkSeconds = dto?.accumulatedWorkSeconds ?? 0;
        const accumulatedPauseSeconds = dto?.accumulatedPauseSeconds ?? 0;

        if (!dto) {
            this.snapshot.set({ uiState: 'inactive', workedSeconds: accumulatedWorkSeconds, pauseSeconds: accumulatedPauseSeconds });
            this.elapsedTimeSec.set(accumulatedWorkSeconds);
            this.totalPauseSec.set(accumulatedPauseSeconds);
            return;
        }

        const isPaused = !!dto.currentPauseStartTime;
        const isRunning = !!dto.currentWorkStartTime && !isPaused;

        if (!isRunning && !isPaused) {
            this.snapshot.set({ uiState: 'inactive', workedSeconds: accumulatedWorkSeconds, pauseSeconds: accumulatedPauseSeconds });
            this.elapsedTimeSec.set(accumulatedWorkSeconds);
            this.totalPauseSec.set(accumulatedPauseSeconds);
            return;
        }

        const uiState: UiState = isPaused ? 'paused' : 'running';
        const workOffset = isRunning ? this.secondsSinceTimeOfDay(dto.currentWorkStartTime) : 0;
        const pauseOffset = isPaused ? this.secondsSinceTimeOfDay(dto.currentPauseStartTime) : 0;

        const worked = accumulatedWorkSeconds + workOffset;
        const paused = accumulatedPauseSeconds + pauseOffset;

        this.snapshot.set({ uiState, workedSeconds: worked, pauseSeconds: paused });
        this.elapsedTimeSec.set(worked);
        this.totalPauseSec.set(paused);
    }

    /** Erwartet 'HH:MM:SS.ffffff' bzw. 'HH:MM:SS.fffffff' für heute. */
    private secondsSinceTimeOfDay(time: string | null): number {
        if (!time) return 0;
        const m = time.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
        if (!m) return 0;
        const h = +m[1], mi = +m[2], s = +m[3];
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, mi, s, 0);
        const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
        return Math.max(0, diff);
    }
}
